import { Injectable } from 'framework/di';
import { Signal, signal } from 'framework/store/signal';

import { DomHelper } from '../../helpers/dom.helper';
import { appIntegration } from '../../integration';
import { AppService } from '../app/app.service';
import { RecommendationModel } from './recommendation.model';
import { RecommendationService } from './recommendation.service';

@Injectable
export class RecommendationWidgetController {
  protected template?: string;
  protected readonly MOBILE_BREAKPOINT: number = 768;

  state: Signal<State>;

  constructor(
    private domHelpers: DomHelper,
    private recommendationService: RecommendationService,
    private appService: AppService
  ) {
    this.state = signal({
      loading: false,
      model: null,
    });
  }

  async connect(props: RecommendationWidgetControllerProps, parent: HTMLElement) {
    const { widgetId } = props;

    const template = await this.loadTemplate();
    this.template = template;

    const unobserve = this.domHelpers.observeElementInViewport(parent, async () => {
      await this.handleWidgetInViewport(widgetId, parent, unobserve);
    });
  }

  /**
   * Loads the widget template.
   * Override this method to customize template loading logic.
   */
  protected async loadTemplate(): Promise<string> {
    return this.recommendationService.getWidgetTemplate();
  }

  /**
   * Handles widget rendering when it comes into viewport.
   * Override this method to customize the entire widget lifecycle.
   */
  protected async handleWidgetInViewport(
    widgetId: string,
    parent: HTMLElement,
    unobserve: () => void
  ): Promise<void> {
    const stateValue = this.state.value;
    stateValue.loading = true;

    const recommendationModel = await this.fetchRecommendationData(widgetId);
    stateValue.loading = false;

    this.state.value = {
      ...stateValue,
    };

    if (!recommendationModel || recommendationModel.properties.products.length === 0) {
      this.handleEmptyRecommendation(parent, unobserve);
      return;
    }

    stateValue.model = recommendationModel;

    this.state.value = {
      ...stateValue,
    };

    await this.renderWidget(recommendationModel, parent);

    this.afterWidgetRender();

    unobserve();
  }

  /**
   * Fetches recommendation data from the service.
   * Override this method to add shop-specific data enrichment or modify API calls.
   */
  protected async fetchRecommendationData(widgetId: string): Promise<RecommendationModel | null> {
    return (await this.recommendationService.getRecommendationModel(widgetId)) || null;
  }

  /**
   * Handles the case when recommendation data is empty.
   * Override this method to customize empty state behavior.
   */
  protected handleEmptyRecommendation(parent: HTMLElement, unobserve: () => void): void {
    parent.remove();
    unobserve();
  }

  /**
   * Renders the widget based on the model configuration.
   * Override this method to add custom widget rendering logic.
   */
  protected async renderWidget(model: RecommendationModel, parent: HTMLElement): Promise<void> {
    if (model.properties.widgetDesignSettings?.layoutDisplay === 'carousel') {
      await this.renderCarouselWidget(parent);
    }
  }

  /**
   * Called after widget rendering is complete.
   * Override this method to add post-render logic like analytics or custom integrations.
   */
  protected afterWidgetRender(): void {
    appIntegration();
  }

  /**
   * Gets the mobile breakpoint value.
   * Override this method to customize the mobile breakpoint per shop.
   */
  protected getMobileBreakpoint(): number {
    return this.MOBILE_BREAKPOINT;
  }

  /**
   * Gets the carousel configuration for desktop view.
   * Override this method to customize carousel behavior.
   */
  protected getCarouselConfig(model: RecommendationModel): SlickOptions {
    const { widgetDesignSettings = {} } = model.properties;
    const isRtl = document.documentElement.getAttribute('dir') === 'rtl';

    return {
      dots: true,
      infinite: true,
      slidesToShow: widgetDesignSettings.numberOfProductPerRow || 4,
      slidesToScroll: widgetDesignSettings.numberOfProductPerRow || 4,
      arrows: false,
      draggable: true,
      useTransform: false,
      focusOnSelect: false,
      focusOnChange: false,
      accessibility: false,
      responsive: [
        {
          breakpoint: this.getMobileBreakpoint(),
          settings: this.getMobileCarouselConfig(),
        },
      ],
      rtl: isRtl,
    };
  }

  /**
   * Gets the carousel configuration for mobile view.
   * Override this method to customize mobile carousel behavior.
   */
  protected getMobileCarouselConfig(): Partial<SlickOptions> {
    return {
      dots: true,
      infinite: true,
      slidesToShow: 2,
      slidesToScroll: 2,
      arrows: false,
      draggable: true,
      focusOnSelect: false,
      focusOnChange: false,
      accessibility: false,
    };
  }

  applyRecommendationCarouselEvent(model: RecommendationModel, container: HTMLElement) {
    const { widgetId } = model.properties;
    const isRtl = document.documentElement.getAttribute('dir') === 'rtl';

    // Get carousel configuration
    const carouselConfig = this.getCarouselConfig(model);

    // Init carousel
    const slider = (
      window.$appJQuery('.boost-sd__slider-container').not('.slick-initialized') as SlickInstance
    ).slick(carouselConfig);

    if (isRtl) {
      window.$appJQuery('.boost-sd__slider-container').css({
        direction: 'rtl',
      });

      window.$appJQuery('.boost-sd__slider-container .slick-slide').css({
        float: 'right',
      });
    }

    /**
     * updateTabindex() is the function to improve the behavior when navigate carousel by keyboard
     * Updates the tabindex and aria-label attributes of the slider container elements.
     *
     * Specifically, this function:
     * - Sets the aria-label attribute for each non-cloned slide to indicate its position (e.g. "Slide 1 of 5").
     * - Sets the tabindex attribute to 0 for the currently active slide and its focusable child elements.
     * - Sets the tabindex attribute to -1 for all other slides and their focusable child elements.
     *
     * This ensures that the slider is accessible and can be navigated using keyboard navigation.
     */
    const updateTabindex = () => {
      const items = container.querySelectorAll('.boost-sd__slider-container .slick-slide');
      const selectors = 'a[href], button:not([disabled]), .boost-sd__radio-label[tabindex]';
      const totalSlides = [...items].filter(
        (item) => !item.classList.contains('slick-cloned')
      ).length;
      let indexOfActiveSlide = 0;

      items.forEach((item) => {
        if (!(item instanceof HTMLElement)) return;
        if (!item.classList.contains('slick-cloned')) {
          indexOfActiveSlide++;
          item.setAttribute('aria-label', `Slide ${indexOfActiveSlide} of ${totalSlides}`);
        }

        const tabIndex = item.classList.contains('slick-active') ? 0 : -1;
        item.tabIndex = tabIndex;
        item.querySelectorAll(selectors).forEach((element) => {
          if (element instanceof HTMLElement) {
            element.tabIndex = tabIndex;
          }
        });
      });
    };

    const syncSlideFocusAfterChange = () => {
      updateTabindex();
      setTimeout(() => {
        window.$appJQuery(`#${widgetId} .slick-current`).trigger('focus');
      }, 400);
    };

    updateTabindex();

    slider.on('afterChange', syncSlideFocusAfterChange);

    this.attachCarouselButtonHandlers(container, slider);
  }

  /**
   * Attaches click handlers to carousel navigation buttons.
   * Override this method to customize button behavior.
   */
  protected attachCarouselButtonHandlers(container: HTMLElement, slider: SlickInstance): void {
    const carouselButtons = container.querySelectorAll('.boost-sd__button--circle');
    carouselButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        const behavior = button.getAttribute('aria-label');
        if (behavior === 'Next') {
          slider.slick('slickNext');
        } else if (behavior === 'Previous') {
          slider.slick('slickPrev');
        }
      });
    });
  }

  async renderCarouselWidget(container: HTMLElement) {
    const jquery = await import('jquery');
    const $appJQuery = jquery.default.noConflict(true);
    window.$appJQuery = $appJQuery;

    await import('slick-carousel');

    const { model } = this.state.value;
    if (model && this.template) {
      const html = await this.appService.templateRender(this.template, {
        ...model.properties,
      });

      container.innerHTML = html;
      this.applyRecommendationCarouselEvent(model, container);
    }
  }
}

export type RecommendationWidgetControllerProps = {
  widgetId: string;
};

type State = {
  loading: boolean;
  model: RecommendationModel | null;
};

/**
 * Slick carousel configuration options.
 * Used for type safety when configuring carousel behavior.
 */
interface SlickOptions {
  dots?: boolean;
  infinite?: boolean;
  slidesToShow?: number;
  slidesToScroll?: number;
  arrows?: boolean;
  draggable?: boolean;
  useTransform?: boolean;
  focusOnSelect?: boolean;
  focusOnChange?: boolean;
  accessibility?: boolean;
  responsive?: Array<{
    breakpoint: number;
    settings: Partial<SlickOptions>;
  }>;
  rtl?: boolean;
}

/**
 * Slick instance interface for type-safe method calls.
 */
interface SlickInstance {
  slick(options: SlickOptions | string, ...args: any[]): SlickInstance;
  on(event: string, handler: () => void): void;
}
